import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { API_BASE } from "@/lib/api";

/**
 * Dependency-free SEO head manager. On every route change it pulls the
 * canonical title / description / canonical URL / Open Graph + JSON-LD for the
 * current path from the backend (`/api/seo/meta`) and applies it to <head>.
 * This keeps the client-rendered head in sync with the server-rendered meta
 * injected by the dev-server middleware (so SPA navigation updates the tab
 * title, OG tags and structured data too).
 *
 * Private routes (admin / login / auth) are skipped so they keep noindex
 * behaviour and never inherit public marketing meta.
 */
const SKIP_PREFIXES = ["/admin", "/login", "/auth"];

function upsertMeta(attr, key, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export default function Seo() {
  const { pathname } = useLocation();

  useEffect(() => {
    if (SKIP_PREFIXES.some((p) => pathname.startsWith(p))) return;
    let cancelled = false;
    fetch(`${API_BASE}/seo/meta?path=${encodeURIComponent(pathname)}`)
      .then((r) => r.json())
      .then((m) => {
        if (cancelled || !m || !m.title) return;
        document.title = m.title;
        upsertMeta("name", "description", m.description);
        upsertMeta("name", "robots", m.robots);
        upsertLink("canonical", m.canonical);
        upsertMeta("property", "og:title", m.title);
        upsertMeta("property", "og:description", m.description);
        upsertMeta("property", "og:url", m.canonical);
        upsertMeta("property", "og:type", m.og_type || "website");
        upsertMeta("property", "og:image", m.image);
        upsertMeta("name", "twitter:title", m.title);
        upsertMeta("name", "twitter:description", m.description);
        upsertMeta("name", "twitter:image", m.image);
        let s = document.getElementById("seo-jsonld");
        if (!s) {
          s = document.createElement("script");
          s.type = "application/ld+json";
          s.id = "seo-jsonld";
          document.head.appendChild(s);
        }
        s.textContent = JSON.stringify(m.jsonld || {});
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [pathname]);

  return null;
}
